import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import axios, { AxiosProgressEvent } from 'axios';
import { getAuthToken, removeAuthToken } from '../../Helper/authToken';

const baseQuery = fetchBaseQuery({
  baseUrl: `${process.env.REACT_APP_APIBaseUrl}/uploads`,
});

// Create an Axios instance
const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_APIBaseUrl}/uploads`,
  headers: {
    Authorization: `Bearer ${getAuthToken()}`,
  },
});

axiosInstance.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Define a callback type for progress
type ProgressCallback = (progressEvent: AxiosProgressEvent) => void;

// Function to handle file upload with progress
const uploadFileWithProgress = (
  formData: FormData,
  onProgress: ProgressCallback
) => {
  return axiosInstance.post('', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: onProgress,
  });
};

// Function to handle multiple file uploads with progress
const multiUploadFileWithProgress = (
  formData: FormData,
  onProgress: ProgressCallback
) => {
  return axiosInstance.post('multiPost', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: onProgress,
  });
};

const getFileSizeFromUrl = async (url: string): Promise<number | null> => {
  try {
    const response = await axios.head(url);
    const contentLength = response.headers['content-length'];
    return contentLength ? parseInt(contentLength, 10) : null;
  } catch (error) {
    console.error('Error fetching file size:', error);
    return null;
  }
};

const uploadApi = createApi({
  reducerPath: 'uploadApi',
  baseQuery: async (args, api, extraOptions) => {
    try {
      const result = await baseQuery(args, api, extraOptions);
      return result;
    } catch (error: any) {
      if (error.status === 401) {
        removeAuthToken();
        window.location.replace('/auth/login');
      }
      throw error;
    }
  },
  endpoints: (builder) => ({
    uploadFile: builder.mutation({
      queryFn: async (
        {
          formData,
          onUploadProgress,
        }: {
          formData: FormData;
          onUploadProgress: ProgressCallback;
        },
        _queryApi,
        _extraOptions
      ) => {
        try {
          const response = await uploadFileWithProgress(
            formData,
            onUploadProgress
          );
          return { data: response.data };
        } catch (error: any) {
          return {
            error: {
              status: error.response?.status,
              data: error.response?.data,
            },
          };
        }
      },
    }),
    multiUploadFile: builder.mutation({
      queryFn: async (
        {
          formData,
          onUploadProgress,
        }: {
          formData: FormData;
          onUploadProgress: ProgressCallback;
        },
        _queryApi,
        _extraOptions
      ) => {
        try {
          const response = await multiUploadFileWithProgress(
            formData,
            onUploadProgress
          );
          return { data: response.data };
        } catch (error: any) {
          return {
            error: {
              status: error.response?.status,
              data: error.response?.data,
            },
          };
        }
      },
    }),
    deleteFile: builder.mutation({
      query: (fileId: string) => {
        return {
          url: `${fileId}`,
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          },
        };
      },
    }),
    wordUploadFile: builder.mutation({
      query: (data: FormData) => {
        return {
          url: 'wordfiletojson',
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: data,
        };
      },
    }),
    mergePdfApi: builder.mutation({
      query: (data: {
        documentIds: string[];
        folderPath?: string;
        documentTitle: string;
      }) => {
        return {
          url: 'multiImgDocToPDFPost',
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          },
        body: {
            documentIds: data.documentIds,
            folderPath: data.folderPath ?? 'mergerPdf',
            documentTitle: data.documentTitle,
          },
        };
      },
    }),
    getFileSize: builder.mutation({
      queryFn: async (url: string) => {
        try {
          const size = await getFileSizeFromUrl(url);
          return { data: size };
        } catch (error: any) {
          return {
            error: {
              status: error.response?.status,
              data: error.response?.data,
            },
          };
        }
      },
    }),
    getBase64Document: builder.mutation({
      query: (docId: string) => {
        return {
          url: `${docId}`,
          method: 'GET',
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
            'Content-type': 'application/json',
          },
        };
      },
    }),
  }),
});
export const {
  useUploadFileMutation,
  useDeleteFileMutation,
  useMultiUploadFileMutation,
  useWordUploadFileMutation,
  useGetFileSizeMutation,
  useGetBase64DocumentMutation,
  useMergePdfApiMutation,
} = uploadApi;

export default uploadApi;
